import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../components/Context/AuthContext";
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  Link,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import API_BASE_URL from "../../utils/api";

import API_URL from "../../config";
import ScheduleSummer2025 from "../ScheduleSummer2025/ScheduleSummer2025";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";

const WorkshopMaterials = () => {
  const { user, setUser } = useContext(AuthContext);
  const [resources, setResources] = useState([]);
  const [welcomePacketResources, setWelcomePacketResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [showCorpus, setShowCorpus] = useState(null);
  const [showPackets, setShowPackets] = useState(false);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/materials`);
      setResources(res.data.resources);
    } catch (err) {
      setError("Failed to load workshop materials.");
    } finally {
      setLoading(false);
    }
  };
  const fetchWelcomePacketResources = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/auth/welcomePacketandSchedule`
      );
      setWelcomePacketResources(res.data?.resources ?? []);
    } catch (err) {
      setError("Failed to load welcome packet materials.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/redeem-workshop-code`,
        {
          code,
        }
      );
      const userRes = await axios.get(`${API_BASE_URL}/auth/me`);
      setUser(userRes.data);
      setSuccess(res.data.message);
      fetchResources(); // fetch materials after access granted
      fetchWelcomePacketResources();
    } catch (err) {
      setError(err.response?.data?.error || "Invalid code.");
    }
  };

  useEffect(() => {
    if (user?.workshopAccess) {
      fetchResources();
      fetchWelcomePacketResources();
    }
  }, [user]);

  return (
    <Box
      sx={{
        width: "auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      {" "}
      <Typography
        variant="h4"
        sx={{
          marginBottom: "30px",

          textAlign: "center",
          paddingBottom: "5px",
          borderBottom: "2px solid #FFD700",
        }}
      >
        Workshop In Detail Schedule and Materials
      </Typography>
      {/* <Alert severity="info">
        This section is exclusive to Summer Workshop attendees.
        <br />
        Access to the workshop materials requires a unique access code.
        <br />
        <strong>Codes have not been distributed yet.</strong> You will receive
        your personal code once the workshop materials are finalized and
        available.
      </Alert> */}
      {!user?.workshopAccess ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This content is only for workshop attendees. Please enter your
            access code.
          </Typography>
          <TextField
            label="Workshop Access Code"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={handleCodeSubmit}>
            Submit Code
          </Button>
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      ) : loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box sx={{ mt: 4, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                cursor: "pointer",
              }}
              onClick={() => setShowPackets(!showPackets)}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
                Workshop Packets & Schedules
              </Typography>
              <IconButton size="small">
                {showPackets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={showPackets}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                {welcomePacketResources.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "220px",
                    }}
                  >
                    {item.title.toLowerCase().endsWith(".pdf") ? (
                      <PictureAsPdfIcon sx={{ mr: 1, color: "red" }} />
                    ) : (
                      <DescriptionIcon sx={{ mr: 1, color: "green" }} />
                    )}
                    <Typography variant="body2">
                      <Link
                        href={item.url}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        sx={{ color: "#1976d2" }}
                      >
                        {item.title}
                      </Link>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                cursor: "pointer",
              }}
              onClick={() => setShowCorpus(!showCorpus)}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
                📚 Related Readings
              </Typography>
              <IconButton size="small">
                {showCorpus ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            {/* Toggleable Corpus List */}
            <Collapse in={showCorpus}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 3,
                }}
              >
                {resources.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "220px",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ mr: 1, color: "red" }} />
                    <Typography variant="body2">
                      <Link
                        href={item.url}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        sx={{ color: "#1976d2" }}
                      >
                        {item.title}
                      </Link>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              💾 Digitization Materials:
            </Typography>
            <Typography variant="body2" sx={{ display: "inline" }}>
              Please refer to the digitization materials throughout the workshop
              —{" "}
              <Link
                href=" https://www.dropbox.com/scl/fo/lau9s6af7geg8uurk78cb/AO6ixIAoohjObmngPRd6gxs?rlkey=o9wush4en9twh0wvq1eilacts&e=1&st=k8253h25&dl=0"
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ color: "#1976d2" }}
              >
                Open Dropbox Folder
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ display: "", marginTop: "20px" }}>
              Refer to this{" "}
              <Link
                href=" https://data2.nhgis.org/main"
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ color: "#1976d2" }}
              >
                URL{" "}
              </Link>{" "}
              for access to{" "}
              <b>summary statistics and GIS files for U.S. censuses.</b>
            </Typography>{" "}
          </Box>

          {/* Schedule below materials */}
          <Box sx={{ mt: 4 }}>
            <ScheduleSummer2025 />
          </Box>
        </>
      )}
    </Box>
  );
};

export default WorkshopMaterials;
